<script module lang="ts">
  import type { Snippet } from 'svelte';
  import type { Response } from '..';
  import { AlternateLinks } from '@agapi/ui/alternate-links';

  interface State {
    scrollPosition: number;
  }
</script>

<script lang="ts">
  import { page } from '$app/state';
  import { afterNavigate, beforeNavigate, replaceState, preloadData } from '$app/navigation';
  import { browser } from '$app/environment';
  import { PUBLIC_BRAND_NAME } from '$env/static/public';
  import { rewriteUrl } from '../../../utils';

  import { JsonLd } from '@agapi/ui/json-ld';

  import { CollectionHeader, FilterSection, ProductGridSection } from '../../../components';
  import { useCollectionContext } from '../../../contexts';
  import { loadProducts } from '../loader';

  const { data, children }: { data: Response; children?: Snippet } = $props();

  const collectionStore = useCollectionContext();

  if (!browser) {
    collectionStore.update(data.products);
  }

  afterNavigate(async (event) => {
    collectionStore.productLoader = loadProducts;

    const isLatestPath = sessionStorage.getItem('latestPath') === page.url.href;
    console.log(
      'here',
      event.type,
      isLatestPath,
      sessionStorage.getItem('latestPath'),
      event.from,
      data
    );

    if (event.type === 'popstate' && isLatestPath) {
      // If the from path has been hard refreshed the collection store will be empty
      if (collectionStore.content.products.length === 0) {
        // This case will cause scroll position to be dispositioned, set to top of page
        window.scrollTo(0, 0);
        collectionStore.update(data.products);
      } else {
        // No change in current collection store
        const state = page.state as State;
        window.scrollTo(0, state.scrollPosition);
      }
    } else if (event.type === 'popstate') {
      console.log('here:popstate', page.url.href);
      // Change in current collection store
      const preload = await preloadData(page.url.href); // Preload data using previous URL with search params
      collectionStore.update(preload.type === 'loaded' ? preload.data.products : data.products);
    } else {
      // Load fresh data load using server
      collectionStore.update(data.products);
    }
  });

  beforeNavigate((event) => {
    const url = page.url;
    sessionStorage.setItem('latestPath', url.href);

    if (event.type === 'link') {
      replaceState(url, { scrollPosition: window.scrollY });
    }
  });

  const schemaData = $derived({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: data.collection.name,
    description: data.collection.description,
    url: `${page.url.origin}${page.url.pathname}`,
    numberOfItems: collectionStore.content.products.length,
    itemListElement: collectionStore.content.products.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: product.name,
        url: `${page.url.origin}/product/${product.handle}`,
        image: product.variants[0]?.images[0]?.url,
        description: product.description.short,
        sku: product.variants[0]?.sku,
        brand: {
          '@type': 'Brand',
          name: product.brand.name
        },
        offers: {
          '@type': 'Offer',
          price: product.variants[0]?.price.amount / 100,
          priceCurrency: product.variants[0]?.price.currency,
          availability:
            product.variants[0]?.stock > 0
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock'
        }
      }
    }))
  });
</script>

<svelte:head>
  <title>{(data.collection.meta?.title ?? data.collection.name) + ' - ' + PUBLIC_BRAND_NAME}</title>
  <meta
    name="description"
    content={data.collection.meta?.description ??
      data?.collection?.description?.replace(/<[^>]*>/g, '')}
  />
  <link rel="canonical" href={page.url.origin + page.url.pathname} />
  <meta
    property="og:title"
    content={`${data.collection.meta?.title ?? data.collection.name + ' - ' + PUBLIC_BRAND_NAME}`}
  />
  <meta
    property="og:description"
    content={data.collection.meta?.description ??
      data?.collection?.description?.replace(/<[^>]*>/g, '')}
  />
  <meta property="og:url" content={page.url.origin + page.url.pathname} />
  <meta property="og:type" content="website" />
  {#if data.collection.imageUrl}
    <meta property="og:image" content={rewriteUrl(page, data.collection.imageUrl)} />
  {/if}
</svelte:head>

<JsonLd data={schemaData} />

<AlternateLinks links={data.collection.alternates ?? {}} />

<CollectionHeader collection={data.collection} />

{@render children?.()}

{#key page.url.pathname}
  <FilterSection />
  <ProductGridSection />
{/key}
